import React from "react";
import { Box, Typography, useTheme, alpha } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

const CreditHistoryChart = ({ data = [], onBarClick, height = 400 }) => {
  const theme = useTheme();

  const processChartData = () => {
    const dailyData = {};
    data.forEach((log) => {
      const date = new Date(log.datetime).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      dailyData[date] = (dailyData[date] || 0) + 1;
    });

    const labels = Object.keys(dailyData);
    const values = Object.values(dailyData);

    return { labels, values, dailyData };
  };

  const { labels, values, dailyData } = processChartData();

  const chartData = {
    labels,
    datasets: [
      {
        label: "Daily Transactions",
        data: values,
        backgroundColor: (context) => {
          const value = context.dataset.data[context.dataIndex];
          const maxValue = Math.max(...values);
          const ratio = value / maxValue;

          if (ratio > 0.7) return "rgba(220, 38, 38, 0.8)";
          if (ratio > 0.4) return "rgba(234, 88, 12, 0.8)";
          return "rgba(34, 197, 94, 0.8)";
        },
        borderColor: (context) => {
          const value = context.dataset.data[context.dataIndex];
          const maxValue = Math.max(...values);
          const ratio = value / maxValue;

          if (ratio > 0.7) return "rgb(220, 38, 38)";
          if (ratio > 0.4) return "rgb(234, 88, 12)";
          return "rgb(34, 197, 94)";
        },
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
        hoverBackgroundColor: (context) => {
          const value = context.dataset.data[context.dataIndex];
          const maxValue = Math.max(...values);
          const ratio = value / maxValue;

          if (ratio > 0.7) return "rgba(220, 38, 38, 1)";
          if (ratio > 0.4) return "rgba(234, 88, 12, 1)";
          return "rgba(34, 197, 94, 1)";
        },
        hoverBorderColor: theme.palette.primary.main,
        hoverBorderWidth: 3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    // barThickness: 100,
    maxBarThickness: 100,
    categoryPercentage: 0.5,
    barPercentage: 0.6,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            family: "'Inter', sans-serif",
            size: 12,
            weight: 600,
          },
          color: theme.palette.text.primary,
        },
      },
      title: {
        display: true,
        text: "Transaction Activity Overview",
        font: {
          size: 18,
          weight: 700,
          family: "'Inter', sans-serif",
        },
        color: theme.palette.primary.main,
        padding: { bottom: 20 },
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.primary.main,
        bodyColor: theme.palette.text.primary,
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        titleFont: {
          size: 13,
          weight: 600,
          family: "'Inter', sans-serif",
        },
        bodyFont: {
          size: 12,
          family: "'Inter', sans-serif",
        },
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context) =>
            `${context.raw} transaction${context.raw !== 1 ? "s" : ""}`,
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "x",
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            family: "'Inter', sans-serif",
            size: 11,
          },
          color: theme.palette.text.secondary,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: alpha(theme.palette.text.secondary, 0.1),
          drawBorder: false,
        },
        ticks: {
          stepSize: 1,
          font: {
            family: "'Inter', sans-serif",
            size: 11,
          },
          color: theme.palette.text.secondary,
          callback: function (value) {
            if (value % 1 === 0) {
              return value;
            }
          },
        },
        border: {
          dash: [4, 4],
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        onBarClick(labels[index], values[index]);
      }
    },
    onHover: (event, elements) => {
      if (event.native.target) {
        event.native.target.style.cursor =
          elements.length > 0 ? "pointer" : "default";
      }
    },
    animation: {
      duration: 1000,
      easing: "easeOutQuart",
    },
    transitions: {
      zoom: {
        animation: {
          duration: 500,
        },
      },
    },
  };

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          height: height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          borderRadius: 2,
          border: `2px dashed ${alpha(theme.palette.text.secondary, 0.2)}`,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No transaction data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: height,
        position: "relative",
        "&:hover": {
          "& .chart-overlay": {
            opacity: 1,
          },
        },
      }}
    >
      <Bar data={chartData} options={chartOptions} />
      <Box
        className="chart-overlay"
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          padding: 1,
          borderRadius: 1,
          margin: 1,
          opacity: 0,
          transition: "opacity 0.3s ease",
          pointerEvents: "none",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          • Scroll to zoom • Drag to pan • Click bars for details
        </Typography>
      </Box>
    </Box>
  );
};

export default CreditHistoryChart;
